import { useTranslation } from 'react-i18next';
import { useFileConvert } from '../../hooks/useFileConvert';
import { useState, useEffect } from 'react';
import Layout from '../../components/feature/Layout';
import ConversionSuccess from '../../components/feature/ConversionSuccess';

export default function PdfToExcelPage() {
  const { t } = useTranslation();
  const {
    file, status, progress, error, fileName,
    setFile, startConversion, download, reset
  } = useFileConvert();
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
    
    // Set page title
    document.title = 'PDF to Excel Converter - Free Online | FreePDFTools';
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Convert PDF files to Excel spreadsheets online for free. Extract tables and data with precision. Fast, secure, and easy to use.');
    
    // Set canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', `${siteUrl}/pdf-to-excel`);
    
    // SoftwareApplication Schema
    const softwareSchema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "PDF to Excel Converter",
      "url": `${siteUrl}/pdf-to-excel`,
      "applicationCategory": "UtilitiesApplication",
      "operatingSystem": "Any",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "description": "Convert PDF files to Excel spreadsheets online for free. Extract tables and data with precision.",
      "featureList": [
        "Accurate table extraction from PDF",
        "Preserve data structure and formatting",
        "Support for complex tables and charts",
        "Secure file processing",
        "No software installation required"
      ],
      "screenshot": `${siteUrl}/og-image.png`,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.7",
        "ratingCount": "12850",
        "bestRating": "5",
        "worstRating": "1"
      }
    };

    // HowTo Schema
    const howToSchema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Convert PDF to Excel",
      "description": "Step-by-step guide to convert PDF files to Excel spreadsheets online",
      "step": [
        {
          "@type": "HowToStep",
          "position": 1,
          "name": "Upload PDF File",
          "text": "Click 'Select File' or drag and drop your PDF file containing tables into the upload area",
          "url": `${siteUrl}/pdf-to-excel#step1`
        },
        {
          "@type": "HowToStep",
          "position": 2,
          "name": "Convert to Excel",
          "text": "The system will automatically extract tables and data from your PDF and convert them to Excel format",
          "url": `${siteUrl}/pdf-to-excel#step2`
        },
        {
          "@type": "HowToStep",
          "position": 3,
          "name": "Download Excel File",
          "text": "Once conversion is complete, download your Excel spreadsheet with all data preserved",
          "url": `${siteUrl}/pdf-to-excel#step3`
        }
      ],
      "totalTime": "PT2M"
    };

    const softwareScript = document.createElement('script');
    softwareScript.type = 'application/ld+json';
    softwareScript.text = JSON.stringify(softwareSchema);
    document.head.appendChild(softwareScript);

    const howToScript = document.createElement('script');
    howToScript.type = 'application/ld+json';
    howToScript.text = JSON.stringify(howToSchema);
    document.head.appendChild(howToScript);

    return () => {
      document.head.removeChild(softwareScript);
      document.head.removeChild(howToScript);
    };
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      startConversion('pdf-to-excel', ['pdf']);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      startConversion('pdf-to-excel', ['pdf']);
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'uploading': return t('upload.uploading');
      case 'converting': return t('upload.converting');
      case 'downloading': return t('upload.preparing');
      default: return '';
    }
  };

  return (
    <Layout>
      <div className="bg-white min-h-screen">
        <div className="w-full bg-gray-50 border-b border-dashed border-gray-300 py-3">
          <div className="max-w-4xl mx-auto text-center text-gray-400 text-sm">{t('ads.topBanner')} 728×90</div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-file-excel-2-line text-3xl text-green-600"></i>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('pdfToExcel.title')}</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('pdfToExcel.description')}</p>
          </div>

          <div className="mb-10">
            {status === 'idle' && (
              <div
                className={`border-2 border-dashed rounded-2xl p-14 text-center transition-all cursor-pointer ${
                  isDragging ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50 hover:border-green-400 hover:bg-green-50/40'
                }`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5 shadow-sm">
                  <i className="ri-upload-cloud-2-line text-4xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('upload.dragDrop')}</h3>
                <p className="text-gray-500 mb-6">{t('upload.or')}</p>
                <label className="inline-block cursor-pointer">
                  <input type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" />
                  <span className="px-8 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors inline-block whitespace-nowrap">
                    {t('upload.selectFile')}
                  </span>
                </label>
                <p className="text-sm text-gray-400 mt-5">{t('upload.fileSize')}</p>
              </div>
            )}

            {(status === 'uploading' || status === 'converting' || status === 'downloading') && (
              <div className="border-2 border-green-200 rounded-2xl p-12 text-center bg-green-50">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className="ri-loader-4-line text-4xl text-green-600 animate-spin"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{getStatusText()}</h3>
                <p className="text-gray-600 mb-4">{file?.name}</p>
                <div className="max-w-md mx-auto">
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                    <div className="bg-green-600 h-3 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                  </div>
                  <p className="text-sm text-gray-500">{progress}%</p>
                </div>
              </div>
            )}

            {status === 'completed' && (
              <ConversionSuccess
                fileName={fileName}
                originalName={file?.name}
                accentColor="green"
                onDownload={download}
                onReset={reset}
              />
            )}

            {status === 'error' && error && (
              <div className="border-2 border-red-200 rounded-2xl p-12 text-center bg-red-50">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className="ri-error-warning-line text-4xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('upload.error')}</h3>
                <p className="text-red-600 mb-6">{error.message}</p>
                <div className="flex gap-4 justify-center">
                  <button onClick={() => file && startConversion('pdf-to-excel', ['pdf'])} className="px-8 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors whitespace-nowrap">{t('upload.retry')}</button>
                  <button onClick={reset} className="px-8 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap">{t('upload.selectAnother')}</button>
                </div>
              </div>
            )}
          </div>

          <div className="w-full bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl py-8 mb-10 text-center text-gray-400 text-sm">
            {t('ads.middleBanner')} 728×90
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { icon: 'ri-table-line', key: 'feature1' },
              { icon: 'ri-file-chart-line', key: 'feature2' },
              { icon: 'ri-shield-check-line', key: 'feature3' },
            ].map((f) => (
              <div key={f.key} className="text-center p-6 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className={`${f.icon} text-xl text-green-600`}></i>
                </div>
                <h3 className="font-semibold text-gray-900">{t(`pdfToExcel.${f.key}`)}</h3>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">{t('upload.howItWorks')}</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3].map((n) => (
                <div key={n} className="text-center">
                  <div className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg">{n}</div>
                  <h3 className="font-semibold text-gray-900 mb-2">{t(`upload.step${n}`)}</h3>
                  <p className="text-sm text-gray-600">{t(`upload.step${n}Desc`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
